import { FieldErrors } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useConsumerCart, useModal } from 'shop/hooks'
import { CHECKOUT_ORIGIN } from 'shop/types'
import {
  CartErrorMessage,
  CartProductError,
  DiscountError,
  DiscountWarning,
  OrderItemWithVatPrices,
  InvalidTimeslotPrepTimeWarning,
  InvalidTimeslotWarning,
  OrderItem
} from 'shop/types/cart'
import { returnToShop } from 'shop/utils/common'
import DiscountErrorModal from '../ErrorModals/DiscountErrorModal'
import CartProductErrorModal from '../Modal/CartProductErrorModal/CartProductErrorModal'
import ErrorModal from '../Modal/ErrorModal'
import { trackMovActioned } from './tracking/helpers'
import { FormFields } from './types'
import { getDiscountErrorWarningValues } from './utils'

// Determines whether error modals trigger checkout page form submission ("PAY") or not ('PAGE")
type ErrorModalType = 'PAGE' | 'PAY' | null

type Props = {
  showProductErrorModal: ErrorModalType
  showDiscountErrorModal: ErrorModalType
  productError: CartProductError | null
  invalidItems: (OrderItem | OrderItemWithVatPrices)[]
  handleProceedWithPriceChange: (submitForm: boolean) => void
  handleRemoveOrderItemsFromCart: (submitForm: boolean) => Promise<void>
  handleRemoveDiscountViaModal: (submitForm: boolean) => void
  handleUpdateOrderItems: (submitForm: boolean) => void
  activeTimeSlotValidation:
    | InvalidTimeslotWarning
    | InvalidTimeslotPrepTimeWarning
    | null
  hasSubtotalDiscount: boolean
  discountErrorWarnings: (DiscountWarning | DiscountError)[]
  formErrors: FieldErrors<FormFields>
}

const CheckoutErrorModals = ({
  showProductErrorModal,
  showDiscountErrorModal,
  productError,
  invalidItems,
  handleProceedWithPriceChange,
  handleRemoveOrderItemsFromCart,
  handleRemoveDiscountViaModal,
  handleUpdateOrderItems,
  activeTimeSlotValidation,
  discountErrorWarnings,
  hasSubtotalDiscount,
  formErrors
}: Props) => {
  const { cart, cartLoading } = useConsumerCart()
  const { isModalOpen, loginModal, timeSlotModal } = useModal()
  const navigate = useNavigate()

  if (!cart) return <></>

  return (
    <>
      {showProductErrorModal && productError && (
        <CartProductErrorModal
          loading={cartLoading}
          origin={CHECKOUT_ORIGIN}
          productError={productError}
          invalidItems={invalidItems}
          storeName={cart?.store.name}
          onContinue={() => {
            if (
              productError.message === CartErrorMessage.PRODUCT_PRICES_CHANGED
            ) {
              return handleProceedWithPriceChange(
                showProductErrorModal === 'PAY'
              )
            } else if (
              productError.message === CartErrorMessage.PRODUCT_LIMIT_REACHED
            ) {
              return handleUpdateOrderItems(showProductErrorModal === 'PAY')
            } else {
              return handleRemoveOrderItemsFromCart(
                showProductErrorModal === 'PAY'
              )
            }
          }}
        />
      )}
      {isModalOpen('login') && loginModal({})}
      {isModalOpen('timeslot') &&
        activeTimeSlotValidation &&
        timeSlotModal(activeTimeSlotValidation)}
      {!!discountErrorWarnings.length && showDiscountErrorModal && (
        <DiscountErrorModal
          errorCode={discountErrorWarnings[0].message}
          values={getDiscountErrorWarningValues(discountErrorWarnings[0])}
          origin={'checkout'}
          checkoutRemoveDiscount={() =>
            handleRemoveDiscountViaModal(showDiscountErrorModal === 'PAY')
          }
          currentFulfillmentType={cart.fulfillment.type}
          storeSlug={cart.store.slug}
        />
      )}
      {!!formErrors?.['checkout_cart'] && (
        <ErrorModal
          errorCode='MOV_ERROR'
          errorMessages={[formErrors['checkout_cart'].message]}
          errorTitle='Minimum value not met'
          primaryButton={{
            onClick: () => {
              trackMovActioned()
              returnToShop(cart?.store.slug, navigate)
            },
            message: 'Add more items'
          }}
          secondaryButton={
            hasSubtotalDiscount
              ? {
                  onClick: () => handleRemoveDiscountViaModal(false),
                  message: 'Remove discount code'
                }
              : undefined
          }
        />
      )}
    </>
  )
}

export default CheckoutErrorModals
